import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, AlertTriangle, Plus, Info, BookOpen, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface PrescriptionSuggestionsProps {
  patientId: string;
  onSelectSuggestion: (suggestion: {
    name: string;
    item_type: string;
    concentration?: string;
    dosage?: string;
    duration?: string;
    usage_route?: string;
    acquisition?: string;
    observations?: string;
  }) => void;
}

export function PrescriptionSuggestions({ patientId, onSelectSuggestion }: PrescriptionSuggestionsProps) {
  const { data: profile } = useQuery({
    queryKey: ["user-profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("profiles")
        .select("*, health_areas(name)")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ["prescription-suggestions", patientId, profile?.health_area_id],
    queryFn: async () => {
      if (!profile?.health_area_id) return null;

      const { data, error } = await supabase.functions.invoke("get-prescription-suggestions", {
        body: {
          patient_id: patientId,
          health_area_id: profile.health_area_id
        },
      });

      if (error) {
        console.error("Error fetching suggestions:", error);
        return null;
      }

      return data;
    },
    enabled: !!patientId,
  });

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.warnings && suggestion.warnings.length > 0) {
      toast.warning("Atenção", {
        description: suggestion.warnings.join(", "),
      });
    }

    onSelectSuggestion({
      name: suggestion.name,
      item_type: suggestion.type || "medication",
      concentration: suggestion.concentration,
      dosage: suggestion.dosage,
      duration: suggestion.duration,
      usage_route: suggestion.usage_route,
      acquisition: suggestion.acquisition,
      observations: suggestion.observations,
    });

    toast.success(`"${suggestion.name}" adicionado com todos os campos preenchidos!`);
  };

  return (
    <Card className="border-cyan-200">
      <CardContent className="pt-6">
        {!profile?.health_area_id ? (
          <div className="text-center py-8 space-y-3">
            <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-muted-foreground">
              Configure sua área de saúde no perfil para receber sugestões personalizadas de IA.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/app/profile'}
              className="mt-2"
            >
              Configurar Área de Saúde
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
            <span className="ml-2 text-sm text-muted-foreground">Gerando sugestões...</span>
          </div>
        ) : suggestions?.suggestions && suggestions.suggestions.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-900">
                <strong>Importante:</strong> Estas são sugestões baseadas em IA.
                <span className="block mt-1">
                  ⚠️ <strong>Revise cuidadosamente</strong> cada sugestão antes de incluir na prescrição.
                  A responsabilidade final é sempre do profissional de saúde.
                </span>
              </p>
            </div>

            <p className="text-sm text-muted-foreground">
              Baseado no perfil do paciente e área de <strong>{profile.health_areas?.name}</strong>:
            </p>

            <div className="space-y-3">
              {suggestions.suggestions.map((suggestion: any, index: number) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:border-cyan-300 transition-all"
                >
                  <div className="space-y-3">
                    {/* Cabeçalho com nome e badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-base">{suggestion.name}</h4>
                      {suggestion.concentration && (
                        <Badge variant="secondary">{suggestion.concentration}</Badge>
                      )}
                      {suggestion.type && (
                        <Badge variant="outline" className="capitalize">{suggestion.type}</Badge>
                      )}
                    </div>

                    {/* Posologia resumida */}
                    <div className="flex flex-wrap gap-2 text-xs">
                      {suggestion.dosage && (
                        <span className="bg-teal-50 px-2 py-1 rounded">📏 {suggestion.dosage}</span>
                      )}
                      {suggestion.duration && (
                        <span className="bg-blue-50 px-2 py-1 rounded">⏱️ {suggestion.duration}</span>
                      )}
                      {suggestion.usage_route && (
                        <span className="bg-purple-50 px-2 py-1 rounded capitalize">🔄 {suggestion.usage_route}</span>
                      )}
                    </div>

                    {/* BASE CIENTÍFICA - COLAPSÁVEL */}
                    {suggestion.reasoning && (
                      <Collapsible>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-between text-teal-700 hover:bg-teal-50 p-2 h-auto"
                          >
                            <span className="flex items-center gap-1 text-xs font-medium">
                              <BookOpen className="h-3 w-3" />
                              Ver base científica
                            </span>
                            <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-3 rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100 mt-2">
                            <p className="text-sm text-teal-900">{suggestion.reasoning}</p>
                            {suggestion.scientific_references && (
                              <p className="text-xs text-teal-600 mt-2 italic">
                                📚 {suggestion.scientific_references}
                              </p>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* Aquisição */}
                    {suggestion.acquisition && (
                      <div className="text-xs text-muted-foreground">
                        🛒 <strong>Aquisição:</strong> {suggestion.acquisition}
                      </div>
                    )}

                    {/* Observações */}
                    {suggestion.observations && (
                      <div className="text-xs text-muted-foreground italic">
                        💡 {suggestion.observations}
                      </div>
                    )}

                    {/* Avisos */}
                    {suggestion.warnings && suggestion.warnings.length > 0 && (
                      <div className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">{suggestion.warnings.join(", ")}</p>
                      </div>
                    )}

                    {/* BOTÃO INSERIR MOVIDO PARA DENTRO DA ÁREA */}
                    <Button
                      size="sm"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Inserir na Receita
                    </Button>
                  </div>

                  {index < suggestions.suggestions.length - 1 && (
                    <Separator className="mt-3" />
                  )}
                </div>
              ))}
            </div>

            {suggestions.warnings && suggestions.warnings.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 mb-1">Avisos Gerais:</p>
                    <ul className="text-sm text-amber-800 space-y-1">
                      {suggestions.warnings.map((warning: string, idx: number) => (
                        <li key={idx}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-muted-foreground">
              Nenhuma sugestão disponível no momento.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Use a captura multimodal para adicionar itens à receita.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
